import { inject, Injectable } from '@angular/core'
import { ApiService } from './api.service'
import {
  ApiClass,
  ApiRiderAssessment,
  ApiSessionPairing,
  ApiSessionRider,
  ApiSessionVolunteer,
  ApiUpdateRiderAssessmentData,
  ApiUpdateRiderAttendanceData,
  ApiUpdateSessionPairingsData,
  ApiUpdateVolunteerAttendanceData,
  ApiVenueTimeslot
} from '../modal/api.modal'
import { map } from 'rxjs'
import {
  AbstractUpcomingClass,
  Assessment,
  Equipment,
  Horse,
  Pairing,
  Timeslot,
  UpcomingClassImpl,
  Venue,
  Weekday
} from '../modal/class.modal'
import {
  AbstractRider,
  Disability,
  Mobility,
  RiderImpl,
  Volunteer
} from '../modal/person.modal'

@Injectable({
  providedIn: 'root'
})
export class ClassService {
  private api = inject(ApiService)

  getCurrentUserClasses(start?: string, end?: string) {
    return this.api.sessions(start, end).pipe(
      map((res) => {
        return res.sessions.map(ClassFactory.createUpClass)
      })
    )
  }

  getClassRiders(classId: number) {
    return this.api.sessionRiders(classId).pipe(
      map((res) => {
        return res.riders.map(ClassFactory.createRider)
      })
    )
  }

  getDisabilityOptions() {
    return this.api.getDisabilityOptions().pipe(
      map((res) => {
        return res.data
      })
    )
  }

  getClassVolunteers(classId: number) {
    return this.api.sessionVolunteers(classId).pipe(
      map((res) => {
        return res.volunteers.map(ClassFactory.createVolunteer)
      })
    )
  }

  getClassEquipments(classId: number) {
    return this.api.sessionEquipments(classId).pipe(
      map((res) => {
        return res.equipments.map((equi) => {
          return {
            id: equi.id,
            name: equi.name_en
          } as Equipment
        })
      })
    )
  }

  getClassHorses(classId: number) {
    return this.api.sessionHorses(classId).pipe(
      map((res) => {
        return res.horses.map((horse) => {
          return {
            id: horse.id,
            name: horse.name
          } as Horse
        })
      })
    )
  }

  updateVolunteersAttendance(classId: number, volunteer: Volunteer[]) {
    let data = {
      session_id: classId,
      attendances: volunteer.map((vol) => {
        return {
          volunteer_id: vol.id,
          status: vol.isAttendance ?? 'absent'
        }
      })
    } as ApiUpdateVolunteerAttendanceData

    return this.api.updateVolunteersAttendance(data).pipe(
      map((res) => {
        return res.status === 'ok'
      })
    )
  }

  updateRiderAttendances(
    classId: number,
    riders: { id?: number; isAttendance?: string }[]
  ) {
    let data = {
      session_id: classId,
      attendances: riders.map((r) => {
        return {
          rider_id: r.id,
          status: r.isAttendance ?? 'absent'
        }
      })
    } as ApiUpdateRiderAttendanceData

    return this.api.updateRiderAttendances(data).pipe(
      map((res) => {
        return res.status === 'ok'
      })
    )
  }

  getRiderAssessment(classId: number, riderId: number) {
    return this.api.getRiderAssessment(classId, riderId).pipe(
      map((res) => {
        return ClassFactory.createAssessment(res.assessment)
      })
    )
  }

  updateRiderAssessment(
    classId: number,
    riderId: number,
    assessment: Assessment
  ) {
    return this.api
      .updateRiderAssessment({
        session_id: classId,
        rider_id: riderId,
        ...assessment,
        physical_change: assessment.physicalChange
      } as ApiUpdateRiderAssessmentData)
      .pipe(
        map((res) => {
          return ClassFactory.createAssessment(res.assessment)
        })
      )
  }

  getClassPairings(classId: number) {
    return this.api.sessionParings(classId).pipe(
      map((res) => {
        return res.pairings.map(ClassFactory.createPairing)
      })
    )
  }

  updateClassPairings(classId: number, pairings: Pairing[]) {
    return this.api
      .updatePairings({
        session_id: classId,
        pairings: pairings.map((pair) => {
          let pairing = {
            rider_id: pair.rider?.id
          } as ApiUpdateSessionPairingsData['pairings'][0]

          if (pair.leader) {
            pairing.leader_id = pair.leader!.id ?? null
          }
          if (pair.sideHelper) {
            pairing.side_helper_id = pair.sideHelper.id ?? null
          }
          if (pair.equipment) {
            pairing.equipment_id = pair.equipment.id ?? null
          }
          if (pair.horse) {
            pairing.horse_id = pair.horse.id ?? null
          }

          return pairing
        })
      } as ApiUpdateSessionPairingsData)
      .pipe(
        map((res) => {
          return res.pairings.map(ClassFactory.createPairing)
        })
      )
  }

  getUserVenueTimeslots() {
    return this.api.getVolunteerTimeslots().pipe(
      map((res) => {
        return ClassFactory.createVenueTimeslots(res.timeslots)
      })
    )
  }

  getUserPreferVenueTimeslots() {
    return this.api.getPreferredTimeslots().pipe(
      map((res) => {
        return ClassFactory.createVenueTimeslots(res.preferred_timeslots)
      })
    )
  }

  updateUserPreferTimeslots(timeslotIds: number[]) {
    return this.api.updatePreferredTimeslots(timeslotIds).pipe(
      map((res) => {
        return res.status === 'ok'
      })
    )
  }
}

class ClassFactory {
  static createUpClass(clazz: ApiClass): AbstractUpcomingClass {
    let courseType = clazz.course_type
    switch (courseType) {
      case 'independent_rider':
        courseType = 'Independent Ride'
        break
      case 'term_rider':
        courseType = 'Term Ride'
        break
      case 'group_rider':
        courseType = 'Group Ride'
        break
    }
    return new UpcomingClassImpl({
      id: clazz.id,
      name: clazz.course_name,
      date: clazz.date,
      weekday: [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday'
      ].indexOf(clazz.weekday) as Weekday,
      time: {
        start: clazz.start_time,
        end: clazz.end_time
      },
      venue: clazz.venue_name,
      type: courseType
    })
  }

  static createRider(ssRider: ApiSessionRider): AbstractRider {
    let rider = ssRider.rider
    let leader = ssRider.leader
    let helper = ssRider.side_helper
    let disabilities = []
    let otherDisabilities
    let mobility: Mobility | undefined
    let otherMobility

    let clean = (v: string | null | undefined) => {
      if (v === null || v === undefined) return undefined
      let s = v.trim()
      return /^n\/a$/i.test(s) ? undefined : v
    }
    if (rider?.asd) {
      disabilities.push(Disability.AUTISM_SPECTRUM_DISORDERS)
    }
    if (rider?.adhd) {
      disabilities.push(Disability.ATTENTION_DEFICIT_AND_HYPERACTIVITY_DISORDER)
    }
    if (rider?.sld) {
      disabilities.push(Disability.SPECIFIC_LEARNING_DIFFICULTIES)
    }
    if (rider?.ind) {
      disabilities.push(Disability.INTELLECTUAL_DISABILITY)
    }
    if (rider?.comd) {
      disabilities.push(Disability.COMMUNICATION_DIFFICULTIES)
    }
    if (rider?.visi) {
      disabilities.push(Disability.VISUAL_IMPAIRMENT)
    }
    if (rider?.heari) {
      disabilities.push(Disability.HEARING_IMPAIRMENT)
    }
    if (rider?.bed) {
      disabilities.push(Disability.BEHAVIORAL_AND_EMOTIONAL_DIFFICULTIES)
    }
    if (rider?.phyd) {
      disabilities.push(Disability.PHYSICAL_DISABILITY)
    }
    if (rider?.others) {
      disabilities.push(Disability.OTHERS)
      otherDisabilities = rider.others
    }
    if (rider?.ambulatory) {
      mobility = rider.ambulatory.split('_')[0] as Mobility
    }
    if (rider?.non_ambulatory) {
      mobility = rider.non_ambulatory.split('_')[0] as Mobility
    }
    if (rider?.ambulatory_other) {
      otherMobility = rider.ambulatory_other
    }
    if (rider?.non_ambulatory_other) {
      otherMobility = rider.non_ambulatory_other
    }

    let gender = rider?.gender?.toUpperCase()
    return new RiderImpl({
      id: rider?.id,
      name: {
        enName: rider?.name_en,
        cnName: rider?.name_zh_hk
      },
      photo: rider?.photo ?? undefined,
      gender: gender?.startsWith('M')
        ? 'male'
        : gender?.startsWith('F')
          ? 'female'
          : undefined,
      birthDate: new Date(rider?.birth),
      age: rider?.age ?? undefined,
      weight: rider?.weight ?? undefined,
      height: rider?.height ?? undefined,
      headSize: rider?.head_size ?? undefined,
      diagnosis: {
        diagnosisStr: clean(rider?.diagnosis),
        disabilities,
        otherDisabilities,
        mobility,
        otherMobility
      },
      physicalDisabilities: clean(rider?.physical_disabilities),
      physicalDisabilitiesOther: clean(rider?.physical_disabilities_other),
      mentalDisabilities: clean(rider?.mental_disabilities),
      mentalDisabilitiesOther: clean(rider?.mental_disabilities_other),
      medication: clean(rider?.medication),
      ridingExperience: clean(rider?.riding_experience),
      nationality: clean(rider?.nationality),
      language: clean(rider?.language),
      agreeTakePhoto: !!rider?.photo_use_rider,
      isAttendance: ssRider?.attendance ?? undefined,
      leaveRequests:
        ssRider?.leave_requests?.map((lr) => {
          return {
            type: lr.type,
            status: lr.status
          }
        }) ?? [],
      assignedVolunteers: {
        leader: {
          id: leader?.id,
          name: {
            enName: leader?.name_en,
            cnName: leader?.name_zh_hk
          }
        },
        helper: {
          id: helper?.id,
          name: {
            enName: helper?.name_en,
            cnName: helper?.name_zh_hk
          }
        }
      },
      assignedEquipment: {
        id: ssRider?.equipment?.id,
        name: ssRider?.equipment?.name
      },
      assignedHorse: {
        id: ssRider?.horse?.id,
        name: ssRider?.horse?.name
      },
      school: {
        name: rider?.school_name ?? undefined
      }
    })
  }

  static createVolunteer(ssVolunteer: ApiSessionVolunteer): Volunteer {
    let isAttendance = ssVolunteer?.attendance
    return {
      id: ssVolunteer?.id,
      name: {
        enName: ssVolunteer?.name_en,
        cnName: ssVolunteer?.name_zh_hk
      },
      isCoach: ssVolunteer?.is_coach,
      isAttendance:
        isAttendance &&
        ['present', 'absent', 'late', 'excused'].includes(isAttendance)
          ? isAttendance
          : undefined
    }
  }

  static createAssessment(assessment: ApiRiderAssessment): Assessment {
    return {
      id: assessment.id,
      communication: assessment.communication,
      confident: assessment.confident,
      enjoyment: assessment.enjoyment,
      horsemanship: assessment.horsemanship,
      physicalChange: assessment.physical_change,
      remark: assessment.remark,
      status: assessment.status,
      assessor: assessment.assessor
        ? {
            id: assessment.assessor.id,
            name: {
              enName: assessment.assessor.name_en,
              cnName: assessment.assessor.name_zh_hk
            }
          }
        : null,
      confirmer: assessment.confirmer
        ? {
            id: assessment.confirmer.id,
            name: {
              enName: assessment.confirmer.name_en,
              cnName: assessment.confirmer.name_zh_hk
            }
          }
        : null
    }
  }

  static createPairing(pairing: ApiSessionPairing): Pairing {
    return {
      id: pairing?.rider_session_id,
      rider: {
        id: pairing?.rider_id,
        name: {
          enName: pairing?.rider_name
        }
      },
      leader: {
        id: pairing?.leader_id,
        name: {
          enName: pairing?.leader_name
        }
      },
      sideHelper: {
        id: pairing?.side_helper_id,
        name: {
          enName: pairing?.side_helper_name
        }
      },
      equipment: {
        id: pairing?.equipment_id,
        name: pairing?.equipment_name
      },
      horse: {
        id: pairing?.horse_id,
        name: pairing?.horse_name
      }
    }
  }

  static createVenue(timeslot: ApiVenueTimeslot): Venue {
    let venue = {
      id: timeslot?.venue_id,
      name: timeslot?.venue_name,
      timeslots: []
    } as Venue

    return venue
  }

  static createTimeslot(timeslot: ApiVenueTimeslot): Timeslot {
    let defLabel = Number.parseInt(timeslot.start_time) > 12 ? 'PM' : 'AM'
    return {
      id: timeslot?.id ?? timeslot?.timeslot_id,
      name: timeslot?.name,
      label: timeslot?.label ?? defLabel,
      weekday: timeslot?.weekday % 7,
      start: timeslot?.start_time,
      end: timeslot?.end_time
    }
  }

  static createVenueTimeslots(timeslots: ApiVenueTimeslot[]): Venue[] {
    let venues = [] as Venue[]
    timeslots.forEach((ts) => {
      let venue = venues.find((v) => v.id === ts.venue_id)

      if (!venue) {
        venue = ClassFactory.createVenue(ts)
        venues.push(venue)
      }

      let slot = ClassFactory.createTimeslot(ts)
      let timeslot = venue.timeslots.find((_ts) => _ts.weekday === slot.weekday)

      if (!timeslot) {
        timeslot = {
          weekday: slot.weekday!,
          slots: []
        }
        venue.timeslots.push(timeslot)
      }
      timeslot.slots.push(slot)
    })
    return venues
  }
}
